<template>
  <div>
    <!-- accordion -->
    <div
      :id="'expand-sms-customization-accordion' + id"
      class="flex w-full cursor-pointer flex-row items-center justify-between gap-2 border px-5 py-[18px] transition-all"
      :class="{
        'border-b-[1px]': state.accordions[props.index].isOpen,
      }"
    >
      <UICheckbox
        :id="'sms-notification-receiver-checkbox-1' + id"
        v-model:checked="eventNotification.state.isActive"
        class="text-sm"
        @change="updateSmsNotification(eventNotification.state)"
      >
        {{ smsReceiverLabels[notification.receiverType] }}
      </UICheckbox>
      <div
        v-if="eventNotification.state.isActive"
        class="flex w-2/3 justify-end"
        role="button"
        @click="handleAccordionClick"
        @keydown="handleAccordionClick"
        tabindex="0"
      >
        <ChevronDownIcon
          v-if="!state.accordions[props.index].isOpen"
          class="h-5 w-5"
        />
        <ChevronUpIcon v-else class="h-5 w-5" />
      </div>
    </div>

    <!-- SMS Notification configurator -->
    <div
      v-if="state.accordions[props.index].isOpen"
      class="flex w-full flex-col border border-t-0 bg-gray-25"
    >
      <div class="p-6">
        <!-- duration picker -->
        <div
          v-if="showDurationPicker"
          :id="'sms-notification-duration-picker' + id"
          class="py-2"
        >
          <span class="font-medium text-gray-700">
            {{ t('eventNotification.sms.durationLabel') }}
          </span>
          <div
            v-for="(d, i) in isFollowupNotifications
              ? eventNotification.state.afterTime
              : eventNotification.state.beforeTime"
            :key="'sms-duration-picker-' + i"
            class="flex flex-row items-center space-x-3"
            :class="{ '!-mb-10 !-mt-5': i === 0, '!-my-10': i > 0 && i < 2 }"
          >
            <DurationPicker
              :id="'sms-before-time-picker' + i + id"
              class="w-1/2"
              :value="String(d.timeOffset)"
              :unit="d.unit === 'minutes' ? 'mins' : d.unit"
              @on-value-change="
                value => {
                  d.timeOffset = value.duration
                  d.unit =
                    value.timeUnit === 'mins' ? 'minutes' : value.timeUnit
                }
              "
            />

            <span class="mt-5 flex items-center">{{
              isFollowupNotifications
                ? t('eventNotification.sms.afterAppointmentEnds')
                : t('eventNotification.sms.beforeAppointmentStarts')
            }}</span>
            <UIButton
              v-if="i > 0"
              id="remove-sms-availability-hour"
              class="m-x-4 mt-4 text-red-600"
              :text="true"
              @click="removeHour(i)"
            >
              <Trash02Icon class="w-4 items-center" />
            </UIButton>
          </div>
          <div
            v-if="shouldEnableAddMore"
            class="!mt-4 w-[15%] py-4"
            role="button"
            @click="addHour"
            @keydown="addHour"
            tabindex="0"
          >
            <a
              use="outline"
              class="mt-5 cursor-pointer bg-white text-blue-500"
              size="large"
            >
              <PlusIcon class="mr-1 w-5" />
              {{ t('eventNotification.sms.addMore') }}
            </a>
          </div>
        </div>

        <!-- SMS template selector -->
        <div class="mb-[20px] flex flex-col gap-2">
          <div class="flex flex-row items-center gap-3">
            <div class="text-sm font-medium text-gray-700">
              {{ t('eventNotification.sms.templateLabel') }}
            </div>

            <div
              v-if="smsTemplateState.showSmsTemplateRefresh"
              class="flex cursor-pointer flex-row items-center gap-1"
              role="button"
              @click="onClickRefreshSmsTemplates"
              @keydown="onClickRefreshSmsTemplates"
              tabindex="0"
            >
              <RefreshCcw01Icon class="w-4 text-primary-500" />
              <div class="cursor-pointer text-sm text-primary-500">
                {{ t('smsTemplate.fetch_new_templates') }}
              </div>
            </div>
          </div>
          <UISelect
            :id="'sms-template-selector' + id"
            :options="smsTemplateOptions"
            :placeholder="t('eventNotification.sms.templatePlaceholder')"
            :value="selectedSmsTemplateValue"
            filterable
            :loading="isFetchingSmsTemplates"
            @update:value="onSelectSmsTemplate"
          >
            <template #action>
              <UIButton
                :id="'create-sms-template' + id"
                type="tertiary"
                class="w-full"
                size="small"
                @click="AppState.showTextSnippetModal = true"
              >
                <div class="flex flex-row items-center gap-2">
                  <PlusIcon class="mr-1 w-5 text-primary-700" />
                  {{ t('smsTemplate.create_new_template') }}
                </div>
              </UIButton>
            </template>
          </UISelect>
        </div>

        <!-- SMS message editor -->
        <div>
          <UIForm
            ref="smsFormRef"
            :rules="smsBodyRules"
            :model="eventNotification.state"
          >
            <div
              ref="templateVariableBannerEl"
              tabindex="-1"
              class="outline-none"
              data-testid="template-variable-validation-banner-anchor"
            >
              <TemplateVariableValidationBanner
                :state="templateVariableBanner"
              />
            </div>
            <!-- SMS body -->
            <div
              class="mb-[-19px] flex w-full flex-row items-center justify-between px-0.5"
              v-if="
                !eventNotification.state.templateId ||
                eventNotification.state.templateId === 'none'
              "
            >
              <div class="text-sm font-medium text-gray-700">
                {{ t('eventNotification.sms.body') }}
                <span style="color: #d92d20">&nbsp;*</span>
              </div>
              <div
                class="flex cursor-pointer flex-row items-center gap-2"
                :class="{ 'pointer-events-none opacity-50': isResetPending }"
                @click="resetToDefault"
              >
                <RefreshCcw01Icon class="w-4 text-primary-600" />
                <div class="text-sm text-primary-600">
                  {{ t('eventNotification.sms.reset_to_default') }}
                </div>
              </div>
            </div>

            <!-- Editable SMS body when no template is selected -->
            <div
              v-if="
                !eventNotification.state.templateId ||
                eventNotification.state.templateId === 'none'
              "
            >
              <UIFormItem
                id="notification-sms-body"
                required="true"
                path="body"
              >
                <div class="relative w-full">
                  <UIInput
                    id="sms-notification-body"
                    v-model="eventNotification.state.body"
                    :rows="5"
                    type="textarea"
                    :disabled="isResetPending"
                    :placeholder="t('eventNotification.sms.bodyPlaceholder')"
                    class="w-full"
                  />
                  <div class="absolute bottom-2 right-2">
                    <CustomVariablePickerV2
                      @update:selected-value="
                        val => insertAtCursor(val, 'sms-notification-body')
                      "
                    />
                  </div>
                </div>
              </UIFormItem>

              <!-- SMS Counter Display -->
              <div
                class="flex items-center justify-end text-sm text-gray-500"
                style="margin-top: -15px"
              >
                <div class="flex items-center gap-2">
                  <span
                    >{{ smsCharacterCount }}
                    {{ t('smsTemplate.characters') }}</span
                  >
                  <span class="text-gray-400">|</span>
                  <span>{{ smsWordCount }} {{ t('smsTemplate.words') }}</span>
                  <span class="text-gray-400">|</span>
                  <span
                    >{{ smsSegmentCount }} {{ t('smsTemplate.segments') }}</span
                  >
                </div>
              </div>
            </div>

            <!-- SMS template preview when template is selected -->
            <div
              v-if="
                eventNotification.state.templateId &&
                eventNotification.state.templateId !== 'none'
              "
            >
              <EditSmsTemplate
                :id="'sms-template-preview' + id"
                class="mb-4"
                :template-id="eventNotification.state.templateId"
                :sms-body="effectiveSmsBody"
                :created-at="selectedTemplateCreatedAt"
                @edit="openEditModal"
              />
            </div>
          </UIForm>
        </div>

        <!-- Additional Phone Numbers for "emails" or "phoneNumbers" receiver type -->
        <UIForm
          v-if="
            eventNotification.state.receiverType === 'emails' ||
            eventNotification.state.receiverType === 'phoneNumbers'
          "
        >
          <UIFormItem
            id="calendar-alert-phone-numbers"
            :label="t('eventNotification.sms.alertPhoneNumbers')"
          >
            <UIInput
              id="calendar-alert-phone-numbers-input"
              v-model="additionalPhoneNumbers"
              :rows="4"
              type="textarea"
              :placeholder="
                t('eventNotification.sms.alertPhoneNumbersPlaceholder')
              "
            />
          </UIFormItem>
        </UIForm>

        <!-- Test SMS -->
        <UIForm
          id="test-sms-form"
          ref="testSmsFormRef"
          :rules="testSmsRules"
          :model="eventNotification"
          class=""
        >
          <UIFormItem
            id="test-phone-number"
            :label="t('eventNotification.sms.testSMSLabel')"
            path="testPhoneNumber"
            class="w-full"
          >
            <UIInput
              :id="'test-phone-number' + id"
              v-model="eventNotification.testPhoneNumber.value"
              type="tel"
              :placeholder="t('eventNotification.sms.testSMSPlaceholder')"
            />
          </UIFormItem>
          <UIButton
            :id="'send-test-sms' + id"
            type="default"
            :disabled="!isEnabledTestSmsButton || isSendingSms"
            @click="sendTestSms"
            >{{ t('eventNotification.sms.sendButtonLabel') }}</UIButton
          >
        </UIForm>
      </div>
    </div>
  </div>

  <!-- SMS Template Selector Modal -->
  <TextSnippetModalWrapper
    :on-cancel="
      () => {
        AppState.showTextSnippetModal = false
        AppState.editingTemplateData = null
      }
    "
    :on-success="onSmsTemplateSelectorSuccess"
  />
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import SmsService from '@/class/services/SmsService'
import { useNotify } from '@/components/composition/notification'
import CustomVariablePickerV2 from '@/components/dynamic/CustomVariablePickerV2.vue'
import { contactLabelSingular } from '@/states/app'
import {
  EventNotification,
  getLocalizedDefaultNotification,
  markNotificationContentCustomized,
  eventNotificationState as state,
} from '@/states/EventNotificationState'
import { NotificationConfig, NotificationType } from '@/types/notification'
import {
  collectInvalidTemplateVariableFields,
  getDedupedTemplateVariableSnippets,
  resolveTemplateVariableWarningKind,
} from '@/utils/templateValidator'
import { formatPhoneForDisplay } from '@/utils/phone-number'
import { validatePhoneNumber } from '@/utils/validation'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  RefreshCcw01Icon,
  Trash02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UICheckbox,
  UIForm,
  UIFormItem,
  UIInput,
  UISelect,
} from '@gohighlevel/ghl-ui'
import { SegmentedMessage } from 'sms-segments-calculator'
import { computed, onMounted, onUnmounted, PropType, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import AppState, { getSmsTemplates } from '../../../states/app'
import DurationPicker from '../DurationPicker.vue'
import EditSmsTemplate from './EditSmsTemplate.vue'
import TextSnippetModalWrapper from './TextSnippetModalWrapper.vue'
import TemplateVariableValidationBanner from './TemplateVariableValidationBanner.vue'
import type { TemplateVariableValidationBannerState } from './templateVariableValidationBannerState'
import {
  buildTemplateVariableBannerState,
  passesTemplateVariableFieldValidation,
  type NotificationMergeFieldInput,
} from './notificationMergeFieldValidation'

const { t } = useI18n()
const { sendNotification } = useNotify()
const testSmsFormRef = ref()
const smsFormRef = ref()
const isSendingSms = ref(false)
const additionalPhoneNumbers = ref()
const isFetchingSmsTemplates = ref(false)
const smsTemplateOptions = ref<any[]>([])
const lastSelectedSmsTemplate = ref('')
const hasPhoneValidationError = ref(false)
const templateVariableBanner = ref<TemplateVariableValidationBannerState>(null)
const templateVariableBannerEl = ref<HTMLElement | null>(null)
const pinnedTemplateVariableBannerFields = ref<NotificationMergeFieldInput[] | null>(null)

function clearPinnedTemplateVariableBanner() {
  pinnedTemplateVariableBannerFields.value = null
}

function showTemplateVariableBannerForFields(fields: NotificationMergeFieldInput[]) {
  pinnedTemplateVariableBannerFields.value = fields
  templateVariableBanner.value = buildTemplateVariableBannerState(fields, t)
}

// Local state for SMS template functionality
const smsTemplateState = ref({
  showSmsTemplateRefresh: false,
})

// Computed property to help maintain template selection
const selectedSmsTemplateValue = computed(() => {
  const templateId = eventNotification.state.templateId || ''
  // If templateId is empty or null, return 'none' to match the dropdown option
  return templateId === '' ? 'None' : templateId
})

const smsReceiverLabels = {
  contact: contactLabelSingular.value,
  guest: t('eventNotification.guest'),
  assignedUser: 'Assigned User',
  emails: 'Additional Phone Numbers',
  phoneNumbers: 'Additional Phone Numbers',
}

const props = defineProps({
  notification: {
    type: Object as PropType<NotificationConfig>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isNotificationsEnabled: {
    type: Boolean,
    default: true,
  },
})

watch(
  () => props.isNotificationsEnabled,
  () => {
    if (!props.isNotificationsEnabled) {
      state.accordions[props.index].isOpen = false
    }
  }
)

watch(
  () => additionalPhoneNumbers.value,
  () => {
    validateAdditionalPhoneNumbers()
  }
)

const eventNotification = new EventNotification(props.notification)
state[props.notification.receiverType] = eventNotification

/** Suppress isDefault flip while applying receiver-level Reset. */
let suppressCustomizedMark = false
const isResetPending = ref(false)

watch(
  () => [eventNotification.state.body, eventNotification.state.templateId],
  () => {
    if (!suppressCustomizedMark) {
      markNotificationContentCustomized(eventNotification.state)
    }
  }
)

/** SMS text for merge validation, counters, preview, and test-send — snippet template body when selected, else custom body. */
const effectiveSmsBody = computed(() => {
  const tid = eventNotification.state.templateId
  if (!tid || tid === 'none') {
    return eventNotification.state.body || ''
  }
  const opt = smsTemplateOptions.value.find(t => t.value === tid)
  const fromTemplate =
    opt?.template &&
    typeof opt.template === 'object' &&
    'body' in opt.template
      ? String((opt.template as { body?: string }).body ?? '')
      : ''
  return fromTemplate || eventNotification.state.body || ''
})

const shouldTrackTemplateVariableErrors = computed(
  () =>
    !!eventNotification.state.isActive &&
    !!state.accordions[props.index]?.isOpen
)

function runSmsTemplateVariableValidation(options?: { forSave?: boolean }): boolean {
  if (!shouldTrackTemplateVariableErrors.value) {
    if (!options?.forSave) {
      templateVariableBanner.value = null
      clearPinnedTemplateVariableBanner()
    }
    return pinnedTemplateVariableBannerFields.value
      ? passesTemplateVariableFieldValidation(pinnedTemplateVariableBannerFields.value)
      : true
  }

  if (pinnedTemplateVariableBannerFields.value) {
    templateVariableBanner.value = buildTemplateVariableBannerState(
      pinnedTemplateVariableBannerFields.value,
      t
    )
    return passesTemplateVariableFieldValidation(pinnedTemplateVariableBannerFields.value)
  }

  const body = effectiveSmsBody.value || ''
  const fieldValidations = collectInvalidTemplateVariableFields([
    {
      text: body,
      fieldLabel: t('eventNotification.sms.templateVariableFieldSmsBody'),
    },
  ])
  if (fieldValidations.length) {
    const kind = resolveTemplateVariableWarningKind(fieldValidations)
    const snippets = getDedupedTemplateVariableSnippets(fieldValidations)
    if (kind === 'markup') {
      templateVariableBanner.value = {
        type: 'plain',
        text: t('eventNotification.templateVariableIssueMarkup'),
      }
    } else if (kind === 'empty') {
      templateVariableBanner.value = {
        type: 'plain',
        text: t('eventNotification.templateVariableIssueEmpty', {
          emptyPair: '{{ }}',
        }),
      }
    } else if (kind === 'single') {
      const value = snippets[0] ?? ''
      templateVariableBanner.value = {
        type: 'plain',
        text: t('eventNotification.templateVariableIssueSingle', { value }),
      }
    } else {
      templateVariableBanner.value = {
        type: 'default',
        invalidSnippets: snippets,
      }
    }
    return false
  }
  templateVariableBanner.value = null
  return true
}

watch(
  effectiveSmsBody,
  () => {
    clearPinnedTemplateVariableBanner()
    runSmsTemplateVariableValidation()
  },
  { immediate: true }
)

watch(shouldTrackTemplateVariableErrors, track => {
  if (!track) {
    templateVariableBanner.value = null
    clearPinnedTemplateVariableBanner()
    return
  }
  runSmsTemplateVariableValidation()
})

async function focusTemplateVariableWarning() {
  await nextTick()
  const bannerEl = templateVariableBannerEl.value
  if (!bannerEl || !templateVariableBanner.value) {
    return
  }
  bannerEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  bannerEl.focus({ preventScroll: true })
}

async function revealTemplateVariableError(fields?: NotificationMergeFieldInput[]) {
  openAccordion(props.index)
  await nextTick()
  if (fields) {
    showTemplateVariableBannerForFields(fields)
  }
  await focusTemplateVariableWarning()
}

defineExpose({
  validateForm,
  focusTemplateVariableWarning,
  revealTemplateVariableError,
  showTemplateVariableBannerForFields,
  getIndex: () => props.index,
})

watch(
  templateVariableBanner,
  (newVal, oldVal) => {
    const hadError = oldVal != null
    const hasError = newVal != null
    if (hasError && !hadError) state.templateVariableErrorCount++
    if (!hasError && hadError) {
      state.templateVariableErrorCount = Math.max(
        0,
        state.templateVariableErrorCount - 1
      )
    }
  }
)

onUnmounted(() => {
  if (templateVariableBanner.value) {
    state.templateVariableErrorCount = Math.max(
      0,
      state.templateVariableErrorCount - 1
    )
  }
})

onMounted(async () => {
  eventNotification.state.isActive = props.notification.isActive
  additionalPhoneNumbers.value =
    eventNotification.state.additionalPhoneNumbers || ''
  await init()
})

const showDurationPicker = computed(() => {
  return [NotificationType.REMINDER, NotificationType.FOLLOWUP].includes(
    props.notification.notificationType
  )
})

const isFollowupNotifications = computed(() => {
  return props.notification.notificationType === NotificationType.FOLLOWUP
})

const isEnabledTestSmsButton = computed(() => {
  return (
    eventNotification.testPhoneNumber?.value &&
    !!effectiveSmsBody.value.trim() &&
    !templateVariableBanner.value
  )
})

const id = computed(() => {
  return `-${props.notification._id}-${props.notification.receiverType}`
})

const shouldEnableAddMore = computed(() => {
  return isFollowupNotifications.value
    ? eventNotification.state.afterTime &&
        eventNotification.state.afterTime?.length < 3
    : eventNotification.state.beforeTime &&
        eventNotification.state.beforeTime?.length < 3
})

const smsCharacterCount = computed(() => {
  const message = effectiveSmsBody.value || ''
  if (!message.trim()) return 0

  try {
    const segmentedMessage = new SegmentedMessage(message)
    return segmentedMessage.numberOfCharacters
  } catch (error) {
    console.error('Error calculating SMS character count:', error)
    return message.length
  }
})

const smsWordCount = computed(() => {
  const message = effectiveSmsBody.value || ''
  if (!message.trim()) return 0
  return message.split(/\s+/).length
})

const smsSegmentCount = computed(() => {
  const message = effectiveSmsBody.value || ''
  if (!message.trim()) return 0

  try {
    const segmentedMessage = new SegmentedMessage(message)
    return segmentedMessage.segmentsCount
  } catch (error) {
    console.error('Error calculating SMS segments:', error)
    return 1
  }
})

// Computed properties for template dates
const selectedTemplateCreatedAt = computed(() => {
  const templateId = eventNotification.state.templateId
  if (!templateId || templateId === 'none') return ''

  const template = smsTemplateOptions.value.find(
    template => template.value === templateId
  )
  return template?.dateAdded || ''
})

const testSmsRules = {
  testPhoneNumber: {
    required: false,
    trigger: ['input', 'blur'],
    validator(_, value) {
      if (value?.value) {
        const phoneNumber = value.value.trim()

        // Allow custom variables in phone numbers
        if (phoneNumber.includes('{{')) {
          hasPhoneValidationError.value = false
          return true
        }

        // Check if phone number starts with + and has valid format
        if (!phoneNumber.startsWith('+')) {
          hasPhoneValidationError.value = true
          return new Error(
            'Phone number must start with a country code (e.g., +1, +91, +44)'
          )
        }

        // Remove + and check if remaining characters are all digits
        const digitsOnly = phoneNumber.slice(1)
        if (!/^\d+$/.test(digitsOnly)) {
          hasPhoneValidationError.value = true
          return new Error(
            'Phone number must contain only digits after the country code'
          )
        }

        // Check minimum length (should be at least 10 digits after country code for most countries)
        if (digitsOnly.length < 10) {
          hasPhoneValidationError.value = true
          return new Error('Phone number is too short')
        }

        // Check maximum length (reasonable upper limit)
        if (digitsOnly.length > 15) {
          hasPhoneValidationError.value = true
          return new Error('Phone number is too long')
        }
      }

      // No error
      hasPhoneValidationError.value = false
      return true
    },
  },
}

const smsBodyRules = {
  body: {
    required: true,
    trigger: ['input', 'blur'],
    validator(rule, value) {
      if (!effectiveSmsBody.value.trim()) {
        return new Error('The SMS message is required')
      }
      return true
    },
  },
}

async function init() {
  setSmsTemplates()

  if (
    eventNotification.state.beforeTime?.length === 0 &&
    !isFollowupNotifications.value
  ) {
    eventNotification.state.beforeTime = [{ timeOffset: 10, unit: 'minutes' }]
  }
  if (
    eventNotification.state.afterTime?.length === 0 &&
    isFollowupNotifications.value
  ) {
    eventNotification.state.afterTime = [{ timeOffset: 1, unit: 'hours' }]
  }
}

// SMS Template Functions
const setSmsTemplates = async (fetchNew = false) => {
  const templates = await getSmsTemplates(fetchNew)
  smsTemplateOptions.value = templates

  if (smsTemplateOptions.value[0]?.value !== 'none') {
    smsTemplateOptions.value.splice(0, 0, {
      label: t('eventNotification.sms.noneTemplate'),
      value: 'none',
    })
  }
}

function onSelectSmsTemplate(value: string) {
  const template = smsTemplateOptions.value.find(
    template => template.value === value
  )

  if (template && template.value !== 'none') {
    eventNotification.state.templateId = template.value

    if (template.template && 'body' in template.template) {
      eventNotification.state.body = template.template.body || ''
    }
  } else if (template && template.value === 'none') {
    eventNotification.state.templateId = ''
  }

  lastSelectedSmsTemplate.value = value
}

const fetchSmsTemplates = async () => {
  try {
    isFetchingSmsTemplates.value = true
    await setSmsTemplates(true)
  } catch (error) {
    console.error('Error in fetching SMS templates', error)
  } finally {
    isFetchingSmsTemplates.value = false
  }
}

const onClickRefreshSmsTemplates = async () => {
  smsTemplateState.value.showSmsTemplateRefresh = false

  // Store the currently selected template ID
  const currentTemplateId = eventNotification.state.templateId

  // Fetch updated templates
  await fetchSmsTemplates()

  // If a template is currently selected, update the message content with refreshed data
  if (currentTemplateId && currentTemplateId !== 'none') {
    const updatedTemplate = smsTemplateOptions.value.find(
      template => template.value === currentTemplateId
    )

    if (
      updatedTemplate &&
      updatedTemplate.template &&
      'body' in updatedTemplate.template
    ) {
      eventNotification.state.body = updatedTemplate.template.body || ''
    }
  }
}

const onSmsTemplateSelectorSuccess = async (templateData?: any) => {
  AppState.showTextSnippetModal = false
  AppState.editingTemplateData = null // Clear editing data

  // Store the currently selected template ID before fetching updates
  const currentTemplateId = eventNotification.state.templateId

  // If templateData is provided from TextSnippetModal, use it
  if (templateData) {
    // Update the SMS body with the new template content
    if (templateData.body || templateData.text) {
      eventNotification.state.body = templateData.body || templateData.text
    }

    // If a template ID is provided, set it
    if (templateData.id || templateData.templateId) {
      eventNotification.state.templateId =
        templateData.id || templateData.templateId
    }
  }

  // Show refresh option and fetch updated templates
  smsTemplateState.value.showSmsTemplateRefresh = true
  await fetchSmsTemplates()

  // If no templateData was provided but we have a current template,
  // update the message content with refreshed template data
  if (!templateData && currentTemplateId && currentTemplateId !== 'none') {
    const updatedTemplate = smsTemplateOptions.value.find(
      template => template.value === currentTemplateId
    )

    if (
      updatedTemplate &&
      updatedTemplate.template &&
      'body' in updatedTemplate.template
    ) {
      eventNotification.state.body = updatedTemplate.template.body || ''
    }
  }
}

const openEditModal = (templateId?: string) => {
  // Use provided templateId or fall back to current template
  const targetTemplateId = templateId || eventNotification.state.templateId

  // Find the current template to prefill data
  const currentTemplate = smsTemplateOptions.value.find(
    template => template.value === targetTemplateId
  )

  // Set up template data for editing in AppState
  AppState.editingTemplateData = {
    _id: currentTemplate?.value, // template id
    data: {
      _id: currentTemplate?.value,
      ...currentTemplate,
    },
  }

  // Open the TextSnippetModal
  AppState.showTextSnippetModal = true
}

watch(
  () => AppState.fetchSmsTemplates,
  async () => {
    if (AppState.fetchSmsTemplates) {
      smsTemplateState.value.showSmsTemplateRefresh = true
      await fetchSmsTemplates()
      AppState.fetchSmsTemplates = false
    }
  }
)

function addHour() {
  if (isFollowupNotifications.value) {
    eventNotification.state.afterTime?.push({
      timeOffset: 10,
      unit: 'minutes',
    })
  } else {
    eventNotification.state.beforeTime?.push({
      timeOffset: 10,
      unit: 'minutes',
    })
  }
}

function removeHour(i: number) {
  if (isFollowupNotifications.value) {
    eventNotification.state.afterTime?.splice(i, 1)
  } else {
    eventNotification.state.beforeTime?.splice(i, 1)
  }
}

async function updateSmsNotification(data: NotificationConfig) {
  const { _id, isActive } = data
  if (!_id) {
    return
  }
  handleAccordionOnCheck(isActive)
  try {
    await EventNotificationService.updateEventNotifications({ _id, isActive })
    // API succeeded - update central state with the payload we sent
    const index = state.smsNotifications.findIndex(
      n => n._id === _id || n.receiverType === data.receiverType
    )
    if (index !== -1) {
      state.smsNotifications[index] = {
        ...state.smsNotifications[index],
        isActive,
      }
    }
  } catch (error) {
    // Revert local state on error
    eventNotification.state.isActive = !isActive
    console.error('Error in update SMS status', error)
  }
}

async function sendTestSms() {
  try {
    await testSmsFormRef.value.getForm().validate()
  } catch (error) {
    console.error('Invalid phone number')
    return
  }
  if (!runSmsTemplateVariableValidation()) {
    return
  }

  try {
    isSendingSms.value = true
    const { testPhoneNumber } = eventNotification

    const smsService = new SmsService({
      to: testPhoneNumber.value,
      message: effectiveSmsBody.value,
    })
    await smsService.sendTestSms()

    // Show success notification
    sendNotification({
      type: 'success',
      title: t('eventNotification.sms.testSmsSuccessTitle'),
      description: t('eventNotification.sms.testSmsSuccessDesc', {
        phone: formatPhoneForDisplay(testPhoneNumber.value),
      }),
      icon: 'CheckCircleIcon',
    })
  } catch (error) {
    console.error('Error in sending test SMS', error)

    // Show error notification
    sendNotification({
      type: 'error',
      title: t('eventNotification.sms.testSmsErrorTitle'),
      description: t('eventNotification.sms.testSmsErrorDesc'),
      icon: 'AlertCircleIcon',
    })
  } finally {
    isSendingSms.value = false
  }
}

function handleAccordionClick() {
  if (!props.isNotificationsEnabled || !eventNotification.state.isActive) {
    return
  }

  if (!state.accordions[props.index].isOpen) {
    openAccordion(props.index)
  } else {
    closeAccordion(props.index)
  }
}

async function handleAccordionOnCheck(isChecked: boolean) {
  state.accordions[props.index].isChecked = isChecked
  const accordion = state.accordions[props.index]
  if (accordion.isChecked) {
    openAccordion(props.index)
  } else {
    closeAccordion(props.index)
  }
}

function openAccordion(index) {
  if (index === null) {
    return
  }
  if (
    state.lastOpenedAccordion !== null &&
    state.lastOpenedAccordion !== index
  ) {
    state.accordions[state.lastOpenedAccordion].isOpen = false
  }
  state.accordions[index].isOpen = true
  state.lastOpenedAccordion = index
}

function closeAccordion(index) {
  if (index === null) {
    return
  }
  state.accordions[index].isOpen = false
  if (state.lastOpenedAccordion === index) {
    state.lastOpenedAccordion = null
  }
}

function validateForm() {
  return new Promise((resolve, reject) => {
    if (!eventNotification.state.isActive) {
      resolve(1)
      return
    }
    if (!runSmsTemplateVariableValidation({ forSave: true })) {
      reject(new Error('template variables'))
      return
    }
    const validationPromise = smsFormRef.value?.getForm().validate()
    const testSmsValidationPromise = testSmsFormRef?.value?.getForm().validate()

    if (validationPromise && testSmsValidationPromise) {
      return Promise.all([validationPromise, testSmsValidationPromise])
        .then(resolve)
        .catch(reject)
    }
    resolve(1)
  })
}

function validateAdditionalPhoneNumbers() {
  const phoneList = (additionalPhoneNumbers.value || '').toString()
  eventNotification.state.additionalPhoneNumbers = phoneList
    .split(/[\s,]+/)
    .filter(phone => phone.trim() !== '' && validatePhoneNumber(phone))
}

function insertAtCursor(value: string, key: string) {
  if (key.includes('sms-notification-body')) {
    eventNotification.state.body = addCustomValue(key, value)
  }
}

function addCustomValue(elementId: string, insert: string | number): string {
  const element = document.getElementById(elementId)
  const inputElement = element?.querySelector(
    'textarea'
  ) as HTMLTextAreaElement | null
  if (inputElement && inputElement instanceof HTMLTextAreaElement) {
    const input = inputElement
    const start = input.selectionStart ?? 0
    const end = input.selectionEnd ?? 0
    const text = input.value
    const insertText = insert.toString()

    input.value = text.substring(0, start) + insertText + text.substring(end)

    input.setSelectionRange(
      start + insertText.length,
      start + insertText.length
    )

    input.focus()
    return input.value
  }
  return ''
}

async function resetToDefault() {
  if (isResetPending.value) {
    return
  }
  isResetPending.value = true
  try {
    const defaultNotification = await getLocalizedDefaultNotification({
      ...props.notification,
      altId: props.notification.altId || state.calendarId,
      altType: props.notification.altType || 'calendar',
      channel: props.notification.channel,
      notificationType: props.notification.notificationType,
      receiverType: props.notification.receiverType,
    })
    if (!defaultNotification) {
      return
    }
    suppressCustomizedMark = true
    try {
      eventNotification.state.body = defaultNotification.body || ''
      eventNotification.state.subject = defaultNotification.subject || ''
      eventNotification.state.templateId = ''
      eventNotification.state.isDefault = true
      await nextTick()
    } finally {
      suppressCustomizedMark = false
    }
  } finally {
    isResetPending.value = false
  }
}
</script>
